import { FC, useState, useRef, useEffect } from "react";
import { InnerBox } from "@/components/elements/InnerBox";
import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import { black } from "@/theme/colors";
import { CASE_BASIC_INFO_TAB, CASE_BASIC_INFO_TAB_LABEL } from "@/features/case/constants/caseBasicInfoTab";
import { CaseInfoItem } from "@/features/case/components/CaseBasicInfo/CaseInfoItem";
import { CaseJobInfo } from "@/features/case/components/CaseBasicInfo/CaseJobInfo";
import { CaseRequestInfo } from "@/features/case/components/CaseBasicInfo/CaseRequestInfo";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";

type SharedCaseBasicInfoProps = {
  jobInfo: TGetJobDto;
  name: string;
  managementNumber?: string | null;
  personInChargeName?: string;
  memo?: string | null;
};

const TAB_ITEMS = [
  { value: CASE_BASIC_INFO_TAB.BASIC_FORM, label: CASE_BASIC_INFO_TAB_LABEL[CASE_BASIC_INFO_TAB.BASIC_FORM] },
  { value: CASE_BASIC_INFO_TAB.JOB_INFO, label: CASE_BASIC_INFO_TAB_LABEL[CASE_BASIC_INFO_TAB.JOB_INFO] },
  { value: CASE_BASIC_INFO_TAB.REQUEST, label: CASE_BASIC_INFO_TAB_LABEL[CASE_BASIC_INFO_TAB.REQUEST] },
];

/** Read-only case basic info for shared (public) view — same UI as CaseBasicInfo */
export const SharedCaseBasicInfo: FC<SharedCaseBasicInfoProps> = ({ jobInfo, name, managementNumber, personInChargeName, memo }) => {
  const [actionTab, setActionTab] = useState(CASE_BASIC_INFO_TAB.BASIC_FORM);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const activeIndex = TAB_ITEMS.findIndex((item) => item.value === actionTab);
    const activeTab = tabRefs.current[activeIndex];
    const container = containerRef.current;

    if (activeTab && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    }
  }, [actionTab]);

  const handleTabClick = (value: number) => {
    setActionTab(value);
  };

  const getTitle = () => {
    return CASE_BASIC_INFO_TAB_LABEL[actionTab];
  };

  return (
    <InnerBox mt={4} showDivider={false}>
      <Stack direction="row" justifyContent="space-between" mb={4}>
        <Typography variant="sub16Bold">{getTitle()}</Typography>
        <SlidingTabContainer ref={containerRef}>
          <SlidingIndicator style={{ left: indicatorStyle.left, width: indicatorStyle.width }} />
          {TAB_ITEMS.map((item, index) => (
            <TabButton
              key={item.value}
              type="button"
              ref={(el: HTMLButtonElement | null) => {
                tabRefs.current[index] = el;
              }}
              onClick={() => handleTabClick(item.value)}
              $isActive={actionTab === item.value}
            >
              <Typography variant={actionTab === item.value ? "body14Semi" : "body14"}>{item.label}</Typography>
            </TabButton>
          ))}
        </SlidingTabContainer>
      </Stack>
      {actionTab === CASE_BASIC_INFO_TAB.BASIC_FORM && (
        <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
          <Grid item xs={4}>
            <CaseInfoItem label="案件名" value={name} />
          </Grid>
          <Grid item xs={4}>
            <CaseInfoItem label="案件管理番号" value={managementNumber} />
          </Grid>
          <Grid item xs={4}>
            <CaseInfoItem label="案件担当者" value={personInChargeName} />
          </Grid>
          {memo && (
            <Grid item xs={12}>
              <CaseInfoItem label="メモ" value={memo} type="textArea" />
            </Grid>
          )}
        </Grid>
      )}
      {actionTab === CASE_BASIC_INFO_TAB.JOB_INFO && <CaseJobInfo jobInfo={jobInfo} isSharedView />}
      {actionTab === CASE_BASIC_INFO_TAB.REQUEST && <CaseRequestInfo jobInfo={jobInfo} isSharedView />}
    </InnerBox>
  );
};

const SlidingTabContainer = styled(Box)`
  position: relative;
  display: flex;
  background-color: ${black[99]};
  border-radius: 24px;
  padding: 4px;
`;

const SlidingIndicator = styled(Box)`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background-color: white;
  border-radius: 20px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  transition:
    left 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`;

const TabButton = styled("button")<{ $isActive: boolean }>`
  position: relative;
  z-index: 1;
  border: none;
  border-radius: 20px;
  height: 36px;
  min-width: 90px;
  padding: 8px 16px;
  background-color: transparent;
  color: ${({ $isActive }) => ($isActive ? black[30] : black[70])};
  cursor: pointer;
  transition: color 0.2s ease;
  font-family: inherit;

  &:hover {
    background-color: transparent;
  }

  &:focus {
    outline: none;
  }
`;
